// Consultancy-firm management surface (PATH-B W40). A super_tenant_admin can create /
// list / edit / deactivate consultancy firms so they enter the tenant-side
// Implementatiepartner directory (consultancy_firm) via the UI instead of raw SQL.
//
// Wired through @sympllex/api-client + TanStack Query against the admin-worker
// (/admin/consultancy-firms — global, RLS-free, platform_admin-gated server-side).
// Reuses the generic catalog client methods (library key "consultancy-firms"); the
// jurisdiction picker reads /admin/jurisdictions via listCatalog("jurisdictions").
// Built on the shadcn design system (visual parity with the tenant app).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LIBRARY = "consultancy-firms";

type Firm = {
  id: string;
  legalName: string;
  tradeName: string | null;
  registrationNumber: string | null;
  primaryJurisdictionId: string;
  is9SprintsCertified: boolean | null;
  defaultSprintCadenceWeeks: number | null;
  isActive: boolean;
  deletedAt: string | null;
  // index signature so Firm satisfies DataTable's Row extends Record<string, unknown> constraint.
  [key: string]: unknown;
};

interface Jurisdiction { id: string; code: string; displayName: string }

export function ConsultancyFirmsRoute() {
  const [editing, setEditing] = useState<Firm | null>(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["catalog", LIBRARY] });

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["catalog", LIBRARY],
    queryFn: () => api.listCatalog(LIBRARY) as Promise<Firm[]>,
  });
  const { data: jurisdictionsData } = useQuery({
    queryKey: ["catalog", "jurisdictions"],
    queryFn: () => api.listCatalog("jurisdictions") as Promise<Jurisdiction[]>,
  });
  const firms = data ?? [];
  const jurisdictions = jurisdictionsData ?? [];
  const jurisdictionLabel = (id: string) => {
    const j = jurisdictions.find((x) => x.id === id);
    return j ? `${j.displayName} (${j.code})` : id;
  };

  const del = useMutation({ mutationFn: (id: string) => api.softDeleteCatalogRow(LIBRARY, id), onSuccess: invalidate });
  const restore = useMutation({ mutationFn: (id: string) => api.restoreCatalogRow(LIBRARY, id), onSuccess: invalidate });

  const columns: Column<Firm>[] = [
    { key: "legalName", header: "Legal name", render: (f) => <span className="font-medium">{f.legalName}</span> },
    { key: "tradeName", header: "Trade name", render: (f) => f.tradeName ?? "—" },
    { key: "primaryJurisdictionId", header: "Jurisdiction", render: (f) => jurisdictionLabel(f.primaryJurisdictionId) },
    { key: "is9SprintsCertified", header: "9-sprints", render: (f) => (f.is9SprintsCertified ? <Badge variant="secondary">yes</Badge> : "—") },
    { key: "defaultSprintCadenceWeeks", header: "Cadence (wk)", align: "right", render: (f) => f.defaultSprintCadenceWeeks ?? "—" },
    { key: "isActive", header: "Active", render: (f) => (f.isActive !== false ? <Badge variant="secondary">yes</Badge> : <Badge variant="outline">no</Badge>) },
  ];

  return (
    <section>
      <PageHeader
        title="Consultancy firms"
        description="Firms created here appear in the tenant-side Implementatiepartner directory."
        actions={
          <>
            {isFetching && <span className="text-sm text-muted-foreground">updating…</span>}
            <Can perm="super_tenant.canonical_library.write">
              <Button onClick={() => { setEditing(null); setCreating(true); }}>+ New firm</Button>
            </Can>
          </>
        }
      />

      {error && <p className="mb-4 text-sm text-destructive">Failed to load: {(error as Error).message}</p>}

      <DataTable
        columns={columns}
        rows={firms}
        loading={isLoading}
        getRowId={(f) => f.id}
        onRowClick={(f) => navigate({ to: "/consultancy-firms/$firmId", params: { firmId: f.id } })}
        empty="No consultancy firms yet."
        rowActions={(firm) => {
          const active = firm.isActive !== false;
          return (
            <Can perm="super_tenant.canonical_library.write" fallback={<span className="text-muted-foreground">—</span>}>
              <div className="flex justify-end gap-1.5">
                <Button size="xs" variant="outline" onClick={() => { setCreating(false); setEditing(firm); }}>Edit</Button>
                {active
                  ? <Button size="xs" variant="destructive" disabled={del.isPending} onClick={() => del.mutate(firm.id)}>Deactivate</Button>
                  : <Button size="xs" variant="outline" disabled={restore.isPending} onClick={() => restore.mutate(firm.id)}>Restore</Button>}
              </div>
            </Can>
          );
        }}
      />
      {(del.isError || restore.isError) && (
        <p className="mt-2 text-sm text-destructive">{errMsg((del.error ?? restore.error) as unknown)}</p>
      )}

      <Can perm="super_tenant.canonical_library.write">
        <FirmDialog
          key={editing ? editing.id : "new"}
          open={creating || editing !== null}
          jurisdictions={jurisdictions}
          editing={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={() => { setCreating(false); setEditing(null); invalidate(); }}
        />
      </Can>
    </section>
  );
}

function FirmDialog({ open, jurisdictions, editing, onClose, onDone }: {
  open: boolean; jurisdictions: Jurisdiction[]; editing: Firm | null; onClose: () => void; onDone: () => void;
}) {
  const isEdit = editing !== null;
  const [legalName, setLegalName] = useState(editing?.legalName ?? "");
  const [tradeName, setTradeName] = useState(editing?.tradeName ?? "");
  const [registrationNumber, setRegistrationNumber] = useState(editing?.registrationNumber ?? "");
  const [primaryJurisdictionId, setPrimaryJurisdictionId] = useState(editing?.primaryJurisdictionId ?? "");
  const [is9SprintsCertified, setIs9SprintsCertified] = useState(editing?.is9SprintsCertified ?? false);
  const [cadence, setCadence] = useState(editing?.defaultSprintCadenceWeeks != null ? String(editing.defaultSprintCadenceWeeks) : "");
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      isEdit ? api.updateCatalogRow(LIBRARY, editing!.id, payload) : api.createCatalogRow(LIBRARY, payload),
    onSuccess: onDone,
  });

  const submit = () => {
    setClientError(null);
    if (!legalName.trim()) { setClientError("Legal name is required."); return; }
    if (!primaryJurisdictionId.trim()) { setClientError("Primary jurisdiction is required."); return; }
    let defaultSprintCadenceWeeks: number | null = null;
    if (cadence.trim()) {
      const n = Number(cadence);
      if (!Number.isInteger(n) || n <= 0) { setClientError("Sprint cadence must be a positive whole number of weeks."); return; }
      defaultSprintCadenceWeeks = n;
    }
    mutation.mutate({
      legalName: legalName.trim(),
      tradeName: tradeName.trim() || null,
      registrationNumber: registrationNumber.trim() || null,
      primaryJurisdictionId,
      is9SprintsCertified,
      defaultSprintCadenceWeeks,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { mutation.reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit consultancy firm" : "New consultancy firm"}</DialogTitle>
          <DialogDescription>Firms created here appear in the tenant-side Implementatiepartner directory.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Legal name *">
            <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} />
          </Field>
          <Field label="Trade name">
            <Input value={tradeName} onChange={(e) => setTradeName(e.target.value)} />
          </Field>
          <Field label="Registration number">
            <Input value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
          </Field>
          <Field label="Primary jurisdiction *">
            {jurisdictions.length > 0 ? (
              <Select value={primaryJurisdictionId} onValueChange={setPrimaryJurisdictionId}>
                <SelectTrigger className="w-full"><SelectValue placeholder="— select —" /></SelectTrigger>
                <SelectContent>
                  {jurisdictions.map((j) => <SelectItem key={j.id} value={j.id}>{j.displayName} ({j.code})</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={primaryJurisdictionId} onChange={(e) => setPrimaryJurisdictionId(e.target.value)} placeholder="jurisdiction id (uuid)" />
            )}
          </Field>
          <Field label="Default sprint cadence (weeks)">
            <Input value={cadence} onChange={(e) => setCadence(e.target.value)} inputMode="numeric" />
          </Field>
          <Field label="9-Sprints certified">
            <div className="flex items-center gap-2 pt-2 text-sm">
              <Checkbox id="firm-9sprints" checked={is9SprintsCertified} onCheckedChange={(c) => setIs9SprintsCertified(c === true)} />
              <Label htmlFor="firm-9sprints" className="font-normal">Certified</Label>
            </div>
          </Field>
        </div>
        {clientError && <p className="text-sm text-destructive">{clientError}</p>}
        {mutation.isError && <p className="text-sm text-destructive">{errMsg(mutation.error as unknown)}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => { mutation.reset(); onClose(); }}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save changes" : "Create firm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function errMsg(e: unknown): string {
  return e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message;
}
